import { useEffect, useState, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import { AtSign, MessageSquareText, AlignLeft, Play, AudioLines, Mic, Globe, File, Brain, BookOpen, Wrench, Settings2, Clock, X } from "lucide-react";
import { AddContextMenu, ContextItem, ContextGroup, AddContextMenuHandle } from "@/components/shared/AddContextMenu";
import { supabase } from "@/integrations/supabase/client";
import { STUDY_TOOLS } from "./studyToolsData";
import { $createMentionNode } from "./MentionNode";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getSelection, $isRangeSelection, $createTextNode, $getRoot } from "lexical";
import { useTranslation } from "@/context/LanguageContext";

export interface AddContextButtonHandle {
  openMenu: () => void;
  closeMenu: () => void;
}

interface AddContextButtonProps {
  onContextSelect?: (item: ContextItem) => void;
  onMentionInsert?: (item: ContextItem) => void;
  onSelectedItemsChange?: (items: ContextItem[]) => void;
}

const getIconForType = (type: string) => {
  switch (type) {
    case "chat":
      return MessageSquareText;
    case "pdf":
    case "document":
    case "doc":
      return AlignLeft;
    case "youtube":
    case "video":
      return Play;
    case "audio":
      return AudioLines;
    case "recording":
      return Mic;
    case "web":
      return Globe;
    default:
      return AlignLeft;
  }
};

const getIconColorForType = (type: string) => {
  switch (type) {
    case "chat":
      return undefined;
    case "pdf":
    case "document":
    case "doc":
      return "#3b82f6";
    case "youtube":
    case "video":
      return "#ef4444";
    case "audio":
      return "#8b5cf6";
    case "recording":
      return "#f59e0b";
    case "web":
      return "#22c55e";
    default:
      return undefined;
  }
};

export const AddContextButton = forwardRef<AddContextButtonHandle, AddContextButtonProps>(
  ({ onContextSelect, onMentionInsert, onSelectedItemsChange }, ref) => {
    const { t } = useTranslation();
    const [groups, setGroups] = useState<ContextGroup[]>([]);
    const [selectedContextItems, setSelectedContextItems] = useState<ContextItem[]>([]);
    const menuRef = useRef<AddContextMenuHandle>(null);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      openMenu: () => menuRef.current?.open(),
      closeMenu: () => menuRef.current?.close(),
    }));

    useEffect(() => {
      const fetchRecentContent = async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;

          const { data: recentContent, error } = await supabase
            .from("content")
            .select("id, title, type")
            .eq("user_id", user.id)
            .order("updated_at", { ascending: false })
            .limit(5);

          if (error) {
            console.error("Error fetching recent content:", error);
            return;
          }

          const recentsItems: ContextItem[] = (recentContent || []).map((content) => ({
            id: content.id,
            label: content.title,
            icon: getIconForType(content.type),
            iconColor: getIconColorForType(content.type),
            group: "Recents",
          }));

          const contextItems: ContextItem[] = [
            { id: "learn-plus", label: "Learn+", icon: Brain, iconColor: "#3cb371", group: "Context" },
            { id: "search", label: "Search", icon: Globe, iconColor: "#3b82f6", group: "Context" },
            { id: "study-mode", label: "Study Mode", icon: BookOpen, iconColor: "#ef4444", group: "Context" },
          ];

          const toolsItems: ContextItem[] = [
            { id: "study-tools", label: "Study tools", icon: Wrench, count: STUDY_TOOLS.length, group: "Other Tools" },
            { id: "chat-instructions", label: "Chat instructions", icon: Settings2, count: 1, group: "Other Tools" },
            { id: "recents", label: "Recents", icon: Clock, count: recentContent?.length || 0, group: "Other Tools" },
          ];

          const newGroups: ContextGroup[] = [];
          
          if (recentsItems.length > 0) {
            newGroups.push({ name: "Recents", items: recentsItems });
          }
          
          newGroups.push({ name: "Context", items: contextItems });
          newGroups.push({ name: "Other Tools", items: toolsItems });

          setGroups(newGroups);
        } catch (error) {
          console.error("Error fetching content:", error);
        }
      };

      fetchRecentContent();
    }, []);

    // Notify parent when selected context items change
    useEffect(() => {
      onSelectedItemsChange?.(selectedContextItems);
    }, [selectedContextItems, onSelectedItemsChange]);

    const handleSelect = useCallback((item: ContextItem) => {
      console.log("Context selected:", item);
      
      // Only insert mention in editor for non-Context/non-Recents items (e.g. study tools)
      if (item.group !== "Context" && item.group !== "Recents") {
        onMentionInsert?.(item);
      }
      
      // Track items from "Context" and "Recents" groups for chips and reference tracking
      if (item.group === "Context" || item.group === "Recents") {
        setSelectedContextItems((prev) => {
          // Toggle: remove if already selected, add if not
          const isSelected = prev.some((selected) => selected.id === item.id);
          if (isSelected) {
            return prev.filter((selected) => selected.id !== item.id);
          } else {
            return [...prev, item];
          }
        });
      }
      
      onContextSelect?.(item);
    }, [onContextSelect, onMentionInsert]);

    const handleRemoveChip = (itemId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedContextItems((prev) => prev.filter((item) => item.id !== itemId));
    };

    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        {/* Add Context Button */}
        <AddContextMenu
          ref={menuRef}
          groups={groups}
          onSelect={handleSelect}
          trigger={
            <button
              type="button"
              className="group mb-1 flex h-7 w-fit items-center gap-1 rounded-full border border-border p-1.5 text-sm text-primary/60 hover:bg-primary/5 hover:text-primary/70 focus:outline-none"
            >
              <AtSign className="h-4 w-4 flex-shrink-0" />
              <span className="mx-0.5 truncate text-xs capitalize">
                {t("common.addContext")}
              </span>
            </button>
          }
        />

        {/* Selected Context Chips */}
        {selectedContextItems.filter((item) => item.group !== "Recents").map((item) => {
          const Icon = item.icon;
          const iconColor = item.iconColor || "#6b7280"; // fallback color
          
          return (
            <div
              key={item.id}
              className="mb-1 inline-flex h-7 items-center gap-1.5 rounded-full px-2 py-1 text-xs"
              style={{
                backgroundColor: `${iconColor}15`, // ~8% opacity for background
                border: `1px solid ${iconColor}40`, // ~25% opacity for border
                color: iconColor, // Full opacity for text
              }}
            >
              <Icon
                className="h-3.5 w-3.5 flex-shrink-0"
                style={{ color: iconColor }}
              />
              <span className="truncate">{item.label}</span>
              <button
                type="button"
                onClick={(e) => handleRemoveChip(item.id, e)}
                className="ml-0.5 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10 transition-colors focus:outline-none"
                aria-label={`Remove ${item.label}`}
              >
                <X 
                  className="h-3 w-3" 
                  style={{ color: iconColor }}
                />
              </button>
            </div>
          );
        })}
      </div>
    );
  }
);